
import { Badge } from '@repo/ui/components/ui/badge';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Separator } from '@repo/ui/components/ui/separator';
import { useUpdateArbreStatusTravaux } from '../../../hooks/use-arbre';
import { AlertTriangle, Calendar, CheckCircle, Clock, Scissors, Settings, Shield, TreePine, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Step } from '../../../shared/types';

interface TravauxDetailsProps {
  formData: { [key: string]: any };
  originalData: any | null;
  arbreId?: number;
  steps?: Step[];
}

export function TravauxDetails({ formData, originalData, arbreId, steps }: TravauxDetailsProps) {
  const updateStatusMutation = useUpdateArbreStatusTravaux();

  // Get arbre ID from originalData if not provided
  const arbreIdFromData = arbreId || originalData?.data?.arbre?.id;

  // Helper function to handle status update
  const handleMarkAsCompleted = async () => {
    if (!arbreIdFromData) {
      toast.error('ID de l\'arbre non disponible');
      return;
    }

    try {
      await updateStatusMutation.mutateAsync({
        ids: [arbreIdFromData],
        status_travaux: true
      });
      toast.success('Travaux marqués comme réalisés');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut des travaux');
      console.error(error);
    }
  };

  // Helper function to safely get array values
  const getArrayValue = (key: string): string[] => {
    const value = formData[key];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  };

  // Helper function to get single value
  const getValue = (key: string, defaultValue: string = 'Non renseigné'): string => {
    return formData[key] || defaultValue;
  };

  // Helper function to get field options and map IDs to labels
  const getFieldOptions = (fieldName: string) => {
    if (!steps) return [];
    const step = steps.find(s => s.id === 'preconisation-travaux');
    const field = step?.fields.find(f => f.name === fieldName);
    return field?.options || [];
  };

  // Helper function to map ID to label
  const mapIdToLabel = (fieldName: string, id: string): string => {
    if (!id || id === 'Non spécifié') return 'Non spécifié';
    const options = getFieldOptions(fieldName);
    const option = options.find(opt => opt.id === id);
    return option?.label || id;
  };

  // Helper function to map array of IDs to array of labels
  const mapIdsToLabels = (fieldName: string, ids: string[]): string[] => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => mapIdToLabel(fieldName, id));
  };

  // Scheduling and urgency data
  const schedulingData = [
    {
      label: "Abattage nécessaire",
      value: mapIdToLabel('preco-travaux-abattage', getValue('preco-travaux-abattage', 'Non spécifié')),
      icon: <AlertTriangle className="w-4 h-4" />,
      type: 'single',
      critical: formData['preco-travaux-abattage'] === 'abattage-simple' || formData['preco-travaux-abattage'] === 'abattage-en-vue-de-remplacement' || formData['preco-travaux-abattage'] === 'abattage-par-demontage'
    },
    {
      label: "Échéance des travaux",
      value: [originalData?.data?.arbre?.user_edited_date_travaux ? new Date(originalData?.data?.arbre?.user_edited_date_travaux).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Non renseigné'],
      icon: <Calendar className="w-4 h-4" />,
      type: 'array'
    },
    {
      label: "Prochaine visite",
      value: originalData?.data?.arbre?.user_edited_date_pro_visite ? [new Date(originalData?.data?.arbre?.user_edited_date_pro_visite).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })] : ["Non renseigné"],

      icon: <Clock className="w-4 h-4" />,
      type: 'single'
    },

  ];

  // Work categories
  const workCategories = [
    {
      title: "Travaux au collet",
      key: 'preco-travaux-collet',
      otherKey: 'preco-travaux-collet_other',
      icon: <TreePine className="w-5 h-5" />,
      color: 'bg-primary/5 border-primary/20'
    },
    {
      title: "Travaux sur le tronc",
      key: 'preco-travaux-tronc',
      otherKey: 'preco-travaux-tronc_other',
      protectionKey: 'preco-travaux-tronc_protection',
      icon: <Shield className="w-5 h-5" />,
      color: 'bg-blue-50 border-blue-200'
    },
    {
      title: "Travaux sur le houppier",
      key: 'preco-travaux-houppier',
      otherKey: 'preco-travaux-houppier_other',
      icon: <Scissors className="w-5 h-5" />,
      color: 'bg-purple-50 border-purple-200'
    }
  ];

  // Comments data
  const comments = [
    {
      label: "Commentaires travaux",
      value: getValue('preco-travaux-com', ''),
      key: 'preco-travaux-com'
    },
    {
      label: "Commentaires généraux",
      value: getValue('travaux_commentaire', ''),
      key: 'travaux_commentaire'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Scheduling and Urgency Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Planification et urgence
            </CardTitle>
            {arbreIdFromData && !originalData?.data?.arbre?.status_travaux && (
              <Button
                onClick={handleMarkAsCompleted}
                disabled={updateStatusMutation.isPending}
                size="sm"
                className="bg-primary hover:bg-primary"
              >
                {updateStatusMutation.isPending ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    En cours...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Action réalisé
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {schedulingData.map((item, index) => (
              <div key={index} className={`p-4 rounded-lg border ${item.critical ? 'bg-red-50 border-red-200' : 'bg-muted/50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`${item.critical ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {item.icon}
                  </div>
                  <span className={`text-sm font-medium ${item.critical ? 'text-red-900' : 'text-muted-foreground'}`}>
                    {item.label}
                  </span>
                </div>
                <div className="ml-6">
                  {item.type === 'array' ? (
                    <div className="flex flex-wrap gap-2">
                      {(item.value as string[]).length > 0 ? (
                        (item.value as string[]).map((val, idx) => (
                          <Badge key={idx} variant={item.critical ? "destructive" : "secondary"}>
                            {val}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground italic">Aucune échéance définie</span>
                      )}
                    </div>
                  ) : (
                    <Badge variant={item.critical ? "destructive" : "secondary"} className="font-medium">
                      {item.value as string}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Work Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {workCategories.map((category, index) => {
          const workItems = getArrayValue(category.key);
          const workItemsLabels = mapIdsToLabels(category.key, workItems);
          const otherWork = getValue(category.otherKey, '');
          const protection = category.protectionKey ? getValue(category.protectionKey, '') : '';
          const hasWork = workItems.length > 0 || otherWork || protection;

          return (
            <Card key={index} className={category.color}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    {category.icon}
                    {category.title}
                  </CardTitle>

                </div>
              </CardHeader>
              <CardContent>
                {hasWork ? (
                  <div className="space-y-3">
                    {/* Standard work items */}
                    {workItems.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-muted-foreground">
                          Interventions prévues
                        </div>
                        <div className="space-y-1">
                          {workItemsLabels.map((itemLabel, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-primary" />
                              <span className="text-sm">{itemLabel}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Protection work (for trunk only) */}
                    {protection && (
                      <>
                        {workItems.length > 0 && <Separator />}
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-muted-foreground">
                            Protection
                          </div>
                          <div className="flex items-center gap-2">
                            <Shield className="w-3 h-3 text-blue-600" />
                            <span className="text-sm">{protection}</span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Other/custom work */}
                    {otherWork && (
                      <>
                        {(workItems.length > 0 || protection) && <Separator />}
                        <div className="space-y-2">
                          <div className="text-sm font-medium text-muted-foreground">
                            Autres interventions
                          </div>
                          <div className="flex items-center gap-2">
                            <Wrench className="w-3 h-3 text-orange-600" />
                            <span className="text-sm">{otherWork}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Settings className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <span className="text-sm text-muted-foreground">
                      Aucun travail prévu
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Comments Section */}
      {(comments?.[0]?.value || comments?.[1]?.value) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Commentaires et observations
              </CardTitle>
              {arbreIdFromData && (
                <Button
                  onClick={handleMarkAsCompleted}
                  disabled={updateStatusMutation.isPending}
                  size="sm"
                  variant="outline"
                  className="bg-background hover:bg-primary/10 border-primary/30 text-primary hover:text-primary"
                >
                  {updateStatusMutation.isPending ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      En cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Action réalisé
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {comments.map((comment, index) => (
                comment.value && (
                  <div key={index} className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">
                      {comment.label}
                    </div>
                    <div className="p-3 rounded-lg border bg-muted/50">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {comment.value}
                      </p>
                    </div>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Card - if no work is planned */}
      {!workCategories.some(cat =>
        getArrayValue(cat.key).length > 0 ||
        getValue(cat.otherKey, '') ||
        (cat.protectionKey && getValue(cat.protectionKey, ''))
      ) && !comments.some(c => c.value) && getValue('preco-travaux-abattage', 'Non spécifié') === 'Non spécifié' && (
          <Card className="border-dashed">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Aucune intervention requise</h3>
                <p className="text-sm text-muted-foreground">
                  Cet arbre ne nécessite actuellement aucun travail d'entretien ou d'intervention.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
